import {
  DisconnectReason,
  LocalTrackPublication,
  Participant,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  RemoteVideoTrack,
  Room,
  RoomEvent,
  Track,
  VideoQuality,
} from 'livekit-client';
import { proxy } from 'valtio';

import { Subscription } from '@libs/subscription';

import {
  callBan,
  connectRoom,
  exchangeTicket,
  parseBanList,
  participantMeta,
  participantRole,
  participantUid,
  RoomService,
} from '@features/video/lib/livekit';
import { VideoLog } from '@features/video/lib/log';
import { decodeTicket } from '@features/video/lib/ticket';
import { Speaking, VideoStreamMode } from '@features/video/types';

const DIRECT_PASS_TIMEOUT_MS = 10000;

// Устройство игрока в комнате глазами судьи: один вход LiveKit
export type RoomDevice = {
  identity: string;
  // userId игрока из метаданных: по нему устройства собираются в игрока
  uid: string;
  // имя из билета: пригодится, если игрока нет в списке участников
  name: string;
  stream: MediaStream | null;
  // подключить трек к <video>: так LiveKit видит размер плитки и просит у SFU слой по нему;
  // возвращает отключение
  attach?: (el: HTMLVideoElement) => () => void;
  micOn: boolean;
  camOn: boolean;
  // роль устройства из имени трека: main — основная камера, extra — дополнительная
  slot?: VideoStreamMode;
  // когда вошло: порядок при равных ролях
  joinedAt: number;
};

// Судья в комнате, включая меня
export type RoomHost = {
  identity: string;
  uid: string;
  name: string;
  isMe: boolean;
  micOn: boolean;
  // userId игрока, с которым судья в приватной комнате
  privateWith?: string;
};

export type RoomSnapshot = { devices: RoomDevice[]; hosts: RoomHost[] };

export type RefereeRoomEvent =
  | { type: 'status'; status: 'connecting' | 'live' | 'reconnecting' }
  // комната закрыта сервером (ended) или связь потеряна (dropped)
  | { type: 'disconnected'; reason: 'ended' | 'dropped' }
  | { type: 'snapshot'; snapshot: RoomSnapshot }
  // браузер не дает играть звук игроков без клика
  | { type: 'audio-blocked'; blocked: boolean };

// Судья в комнате LiveKit: публикует микрофон, слушает всех и смотрит игроков,
// управляет комнатой через RoomService и черный список через ban.php.
// Подписки: все треки guest, звук других host — судьи слышат друг друга
export class RefereeRoomClient {
  readonly events = new Subscription<RefereeRoomEvent>();

  private room: Room | null = null;
  private service: RoomService | null = null;
  private micPub: LocalTrackPublication | null = null;
  private micOn = true;
  // контекст общего шумодава ждет клика (см. MicSource): трек в эфире замьючен при
  // включенном микрофоне
  private gestureNeeded = false;
  // когда началось восстановление связи: длительность в журнал
  private reconnectingAt = 0;
  private audioEls = new Set<HTMLMediaElement>();
  // видеопотоки устройств по identity: собираются из подписанных треков
  private streams = new Map<string, MediaStream>();
  // подключение трека к плитке, одно на трек: плитка переподключается только при смене трека
  private attachers = new Map<string, RoomDevice['attach']>();
  private connecting = false;
  private audioOn = true;
  private videoOn = true;
  // закрепленные игроки: их видео идет по размеру плитки, остальным — потолок в младший слой
  private pinned = new Set<string>();
  // игроки с плитками на странице: видео подписываем только им; null — всем (приват)
  private shown: Set<string> | null = null;
  private server = '';
  private ticket = '';

  // журнал в Datadog с контекстом этой комнаты; gridLayerCap — видео незакрепленных игроков
  // не выше младшего слоя (сетка), в приватной комнате слой выбирает только размер плитки
  constructor(
    private readonly log: VideoLog,
    private readonly gridLayerCap = true,
    // горячее: identity говорящих, отдельно от снимка — иначе страница перерисовывалась бы
    // на каждую смену говорящего; хук держит один объект на все комнаты, клиент ведет в нем
    // только ключи своих участников
    readonly speakingProxy: Speaking = proxy<Speaking>({}),
  ) {}

  // Источник микрофона ждет клика или снова работает: мьют трека в эфире следом
  setGestureNeeded(on: boolean): void {
    this.gestureNeeded = on;
    this.syncMute().catch(() => {});
  }

  // Клик по плашке: включить звук игроков, который браузер не дал запустить сам
  startAudio(): void {
    this.room?.startAudio().catch(() => {});
  }

  get isConnected(): boolean {
    return this.room !== null;
  }

  // track — клон чистого трека общего источника микрофона; null — без микрофона
  async connect(ticket: string, track: MediaStreamTrack | null, micOn: boolean): Promise<void> {
    const claims = decodeTicket(ticket);
    if (!claims) throw new Error('Некорректный билет');
    this.server = claims.aud;
    this.ticket = ticket;

    this.emit({ type: 'status', status: 'connecting' });
    this.log.set({ server: claims.aud, room: claims.room });
    const tokenData = await exchangeTicket(claims.aud, ticket, this.log);
    if (tokenData.role !== 'host') {
      this.log.error('token_rejected', new Error(`role ${tokenData.role}`), { server: claims.aud });
      throw new Error('Билет не дает прав судьи');
    }

    this.connecting = true;
    try {
      this.room = await connectRoom(
        () => this.createRoom(),
        tokenData,
        DIRECT_PASS_TIMEOUT_MS,
        this.log,
      );
    } catch {
      this.room = null;
      throw new Error('Не удалось подключиться к серверу');
    } finally {
      this.connecting = false;
    }
    this.service = new RoomService(claims.aud, tokenData.token, tokenData.room);
    this.micOn = micOn;
    try {
      await this.publishMic(track);
    } catch (e) {
      this.log.error('publish_failed', e);
      throw e;
    }

    this.syncSubscriptions();
    this.emit({ type: 'status', status: 'live' });
    this.publishSnapshot();
  }

  async setMicOn(on: boolean): Promise<void> {
    this.micOn = on;
    if (!this.micPub) return;
    await this.syncMute();
    this.publishSnapshot();
  }

  // Источник дал новый чистый трек (другое устройство, цепочка заново): публикуем новый клон,
  // старый клон останавливаем вместе с публикацией
  async replaceMic(track: MediaStreamTrack | null): Promise<void> {
    const room = this.room;
    if (!room) return;
    if (this.micPub?.track) {
      await room.localParticipant.unpublishTrack(this.micPub.track, true);
      this.micPub = null;
    }
    await this.publishMic(track);
    this.log.event('tracks_replaced');
    this.publishSnapshot();
  }

  // Фокус в другой комнате: эту не слушаем
  setAudioOn(on: boolean): void {
    this.audioOn = on;
    this.audioEls.forEach((el) => {
      el.muted = !on;
    });
  }

  // Видео игроков не нужно, пока открыта модалка привата: экономим канал
  setVideoOn(on: boolean): void {
    this.videoOn = on;
    this.syncSubscriptions();
  }

  // Закрепленным снимаем потолок слоя: дальше слой выбирает размер плитки
  setPinned(userIds: string[]): void {
    this.pinned = new Set(userIds);
    this.syncSubscriptions();
  }

  // Видео только игрокам с плитками на странице: трек без плитки не на паузе, а не подписан.
  // Ручная пауза (`setEnabled`) навсегда перекрывает видимость adaptiveStream, и плитка,
  // подключенная позже, трек уже не включит — так пропадали камеры до перезагрузки
  setShown(userIds: string[] | null): void {
    this.shown = userIds && new Set(userIds);
    this.syncSubscriptions();
  }

  // Устройство в этой комнате: по нему действие уходит в нужный клиент мультикомнаты
  hasParticipant(identity: string): boolean {
    return this.room?.remoteParticipants.has(identity) ?? false;
  }

  // Выключить микрофон устройства игрока; обратно включить может только игрок
  async muteDevice(identity: string): Promise<void> {
    const p = this.room?.remoteParticipants.get(identity);
    const pub = p?.getTrackPublication(Track.Source.Microphone);
    if (!p || !pub || !this.service) return;
    await this.traced('mute', this.service.mute(identity, pub.trackSid, true));
  }

  // Своя пометка «в привате с игроком» для других судей; без userId — снять
  async setPrivateWith(userId?: string): Promise<void> {
    const local = this.room?.localParticipant;
    if (!local || !this.service) return;
    const meta = participantMeta(local);
    if (userId) meta.privateWith = userId;
    else delete meta.privateWith;
    await this.traced('private_with', this.service.updateMetadata(local.identity, meta));
  }

  ban(uid: string): Promise<unknown> {
    return this.traced('ban', callBan(this.server, this.ticket, 'ban', uid));
  }

  unban(uid: string): Promise<unknown> {
    return this.traced('unban', callBan(this.server, this.ticket, 'unban', uid));
  }

  async banList(): Promise<string[]> {
    return parseBanList(await this.traced('ban_list', callBan(this.server, this.ticket, 'list')));
  }

  disconnect(): void {
    this.connecting = false;
    this.micPub = null;
    this.gestureNeeded = false;
    this.setSpeakers([]);
    this.service = null;
    this.audioEls.forEach((el) => el.remove());
    this.audioEls.clear();
    this.streams.clear();
    this.attachers.clear();
    this.room?.disconnect();
    this.room = null;
  }

  /*
      ____,-------------------------------,____
      \   |            Private            |   /
      /___|-------------------------------|___\
  */

  private emit(event: RefereeRoomEvent): void {
    this.events.publish(event);
  }

  // Запрос к серверу связи (twirp, ban.php): провал в журнал, дальше наверх
  private async traced<T>(action: string, request: Promise<T>): Promise<T> {
    try {
      return await request;
    } catch (e) {
      this.log.error('service_failed', e, { action });
      throw e;
    }
  }

  // Трек в эфире замьючен, пока микрофон выключен или контекст шумодава ждет клика:
  // остальные видят «выключен», а не живой значок с тишиной
  private async syncMute(): Promise<void> {
    const pub = this.micPub;
    if (!pub) return;
    const muted = !this.micOn || this.gestureNeeded;
    if (pub.isMuted === muted) return;
    await (muted ? pub.mute() : pub.unmute());
  }

  // Публикуем трек как есть: это клон чистого трека общего источника, мьют — его enabled
  private async publishMic(track: MediaStreamTrack | null): Promise<void> {
    const room = this.room;
    if (!room || !track) return;
    this.micPub = await room.localParticipant.publishTrack(track, {
      source: Track.Source.Microphone,
    });
    await this.syncMute();
  }

  // Ключи говорящих этой комнаты в общем на все комнаты объекте: чужие не трогаем
  private setSpeakers(ids: string[]): void {
    const room = this.room;
    const own = room ? [room.localParticipant.identity, ...room.remoteParticipants.keys()] : [];
    replaceKeys(this.speakingProxy, ids, own);
  }

  private createRoom(): Room {
    // adaptiveStream: слой по размеру плитки в физических пикселях экрана — сетка получает
    // 360p, закрепленная плитка и приват — 720p только на HiDPI или при зуме страницы;
    // невидимые плитки не качаются вовсе
    const room = new Room({ adaptiveStream: { pixelDensity: 'screen' }, dynacast: true });

    const refresh = () => {
      this.syncSubscriptions();
      this.publishSnapshot();
    };
    room.on(RoomEvent.ParticipantConnected, refresh);
    room.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
      // ушедший больше не говорит: его ключ не снимет ни одно событие
      delete this.speakingProxy[p.identity];
      refresh();
    });
    room.on(RoomEvent.TrackPublished, refresh);
    room.on(RoomEvent.TrackUnpublished, refresh);
    room.on(RoomEvent.ParticipantMetadataChanged, refresh);
    room.on(RoomEvent.TrackMuted, () => this.publishSnapshot());
    room.on(RoomEvent.TrackUnmuted, () => this.publishSnapshot());
    room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      this.setSpeakers(speakers.map((p) => p.identity));
    });

    room.on(
      RoomEvent.TrackSubscribed,
      (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Audio) {
          const el = track.attach();
          el.muted = !this.audioOn;
          document.body.appendChild(el);
          this.audioEls.add(el);
        } else if (track instanceof RemoteVideoTrack) {
          this.streams.set(participant.identity, new MediaStream([track.mediaStreamTrack]));
          this.attachers.set(participant.identity, (el) => {
            track.attach(el);
            return () => track.detach(el);
          });
        }
        this.publishSnapshot();
      },
    );
    room.on(
      RoomEvent.TrackUnsubscribed,
      (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Audio) {
          track.detach().forEach((el) => {
            el.remove();
            this.audioEls.delete(el);
          });
        } else if (track.kind === Track.Kind.Video) {
          this.streams.delete(participant.identity);
          this.attachers.delete(participant.identity);
        }
        this.publishSnapshot();
      },
    );

    // Браузер не дает играть звук без клика: плашка, а первый клик где угодно включит сам
    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      const blocked = !room.canPlaybackAudio;
      if (blocked) {
        this.log.event('audio_blocked');
        document.addEventListener('click', () => room.startAudio().catch(() => {}), { once: true });
      }
      this.emit({ type: 'audio-blocked', blocked });
    });

    room.on(RoomEvent.Reconnecting, () => {
      this.reconnectingAt = Date.now();
      this.log.event('reconnecting');
      this.emit({ type: 'status', status: 'reconnecting' });
    });
    room.on(RoomEvent.Reconnected, () => {
      this.log.event('reconnected', { durationMs: Date.now() - this.reconnectingAt });
      refresh();
      this.emit({ type: 'status', status: 'live' });
    });
    room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      // провал прохода внутри connect разбирает сам цикл
      if (this.connecting || reason === DisconnectReason.CLIENT_INITIATED) return;
      const ended =
        reason === DisconnectReason.ROOM_DELETED || reason === DisconnectReason.ROOM_CLOSED;
      this.log.event('disconnected', {
        reason: reason === undefined ? 'unknown' : (DisconnectReason[reason] ?? String(reason)),
        ended,
      });
      this.emit({ type: 'disconnected', reason: ended ? 'ended' : 'dropped' });
    });

    return room;
  }

  // Игроки: звук и видео (видео — пока оно нужно и только тем, чьи плитки на странице);
  // другие судьи: только звук. Потолок слоя — верхняя граница поверх adaptiveStream: меньшее
  // из двух побеждает; закрепленным потолок — старший слой, то есть его нет
  private syncSubscriptions = (): void => {
    const room = this.room;
    if (!room) return;
    room.remoteParticipants.forEach((p) => {
      const isHost = participantRole(p) === 'host';
      const uid = participantUid(p);
      const capped = this.gridLayerCap && !this.pinned.has(uid);
      const shown = this.shown === null || this.shown.has(uid);
      p.trackPublications.forEach((pub) => {
        const isVideo = !isHost && pub.kind === Track.Kind.Video;
        const want = pub.kind === Track.Kind.Audio || (isVideo && this.videoOn && shown);
        if (want && !pub.isSubscribed) pub.setSubscribed(true);
        else if (!want && pub.isSubscribed) pub.setSubscribed(false);
        if (isVideo && want && this.gridLayerCap) {
          const cap = layerCap(pub, capped);
          if (cap !== undefined) pub.setVideoQuality(cap);
        }
      });
    });
  };

  private publishSnapshot(): void {
    const room = this.room;
    if (!room) return;
    const devices: RoomDevice[] = [];
    const hosts: RoomHost[] = [];
    const local = room.localParticipant;
    hosts.push({
      identity: local.identity,
      uid: participantUid(local),
      name: local.name || '',
      isMe: true,
      micOn: this.micPub ? !this.micPub.isMuted : false,
      privateWith: participantMeta(local).privateWith,
    });
    room.remoteParticipants.forEach((p) => {
      if (participantRole(p) === 'host') {
        hosts.push({
          identity: p.identity,
          uid: participantUid(p),
          name: p.name || '',
          isMe: false,
          micOn: isOn(p, Track.Source.Microphone),
          privateWith: participantMeta(p).privateWith,
        });
      } else {
        devices.push({
          identity: p.identity,
          uid: participantUid(p),
          name: p.name || '',
          stream: this.streams.get(p.identity) ?? null,
          attach: this.attachers.get(p.identity),
          micOn: isOn(p, Track.Source.Microphone),
          camOn: isOn(p, Track.Source.Camera),
          slot: deviceSlot(p),
          joinedAt: p.joinedAt?.getTime() ?? 0,
        });
      }
    });
    this.emit({ type: 'snapshot', snapshot: { devices, hosts } });
  }
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Трек опубликован и не замьючен
function isOn(p: Participant, source: Track.Source): boolean {
  const pub = p.getTrackPublication(source);
  return !!pub && !pub.isMuted;
}

// Роль устройства из имени трека, которое ставит сам игрок при публикации
function deviceSlot(p: Participant): VideoStreamMode | undefined {
  const pub =
    p.getTrackPublication(Track.Source.Camera) ?? p.getTrackPublication(Track.Source.Microphone);
  const name = pub?.trackName;
  return name === 'main' || name === 'extra' ? name : undefined;
}

// Потолок слоя: младший или старший из опубликованных слоев. Нельзя слать VideoQuality.HIGH
// как «без потолка»: с двумя слоями симулькаста LiveKit помечает их LOW и MEDIUM, слоя HIGH
// нет, и клиент шлет такой запрос серверу как есть, минуя размер плитки — сервер отдавал бы
// верхний слой всегда. Запрос с качеством существующего слоя клиент сверяет с размером плитки
function layerCap(pub: RemoteTrackPublication, lowest: boolean): VideoQuality | undefined {
  const qualities = pub.trackInfo?.layers.map((l) => l.quality) ?? [];
  if (qualities.length === 0) return undefined;
  return (lowest ? Math.min(...qualities) : Math.max(...qualities)) as VideoQuality;
}

// Ключи горячего объекта ровно по списку: лишние снимаем, недостающие ставим; valtio
// уведомляет только по изменившимся ключам
function replaceKeys(speakingProxy: Speaking, ids: string[], own: Iterable<string>): void {
  const next = new Set(ids);
  for (const id of own) {
    if (!next.has(id)) delete speakingProxy[id];
  }
  next.forEach((id) => {
    speakingProxy[id] = true;
  });
}
